import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  DollarSign, 
  Clock, 
  CheckCircle, 
  XCircle, 
  ArrowUpRight,
  Wallet,
  Loader2,
  RefreshCw,
  AlertCircle,
  History
} from 'lucide-react';

// Usar o mesmo domínio que a página está sendo servida (funciona em produção e preview)
const API = typeof window !== 'undefined' ? window.location.origin : '';

/**
 * WithdrawalSection - Seção de saques no dashboard do criador
 */
const WithdrawalSection = () => {
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [data, setData] = useState({
    withdrawals: [],
    total_earnings: 0,
    total_withdrawn: 0,
    available_balance: 0,
    min_withdrawal: 50
  });
  const [showForm, setShowForm] = useState(false);
  const [amount, setAmount] = useState('');

  const getToken = () => localStorage.getItem('token');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const response = await axios.get(`${API}/api/withdrawals/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestWithdrawal = async (e) => {
    e.preventDefault();
    
    const numAmount = parseFloat(amount);
    if (!numAmount || numAmount < data.min_withdrawal) {
      toast.error(`Valor mínimo para saque: R$ ${data.min_withdrawal.toFixed(2)}`);
      return;
    }
    
    if (numAmount > data.available_balance) {
      toast.error('Saldo insuficiente');
      return;
    }

    try {
      setRequesting(true);
      const token = getToken();
      await axios.post(
        `${API}/api/withdrawals/request`,
        { amount: numAmount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Solicitação de saque enviada!');
      setShowForm(false);
      setAmount('');
      loadData();
    } catch (error) {
      console.error('Erro ao solicitar saque:', error);
      toast.error(error.response?.data?.detail || 'Erro ao solicitar saque');
    } finally {
      setRequesting(false);
    }
  };

  const handleCancelWithdrawal = async (withdrawalId) => {
    if (!confirm('Cancelar esta solicitação de saque?')) return;
    
    try {
      const token = getToken();
      await axios.delete(`${API}/api/withdrawals/${withdrawalId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Solicitação cancelada');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cancelar');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', icon: Clock, label: 'Pendente' },
      approved: { bg: 'bg-blue-500/20', text: 'text-blue-400', icon: CheckCircle, label: 'Aprovado' },
      paid: { bg: 'bg-green-500/20', text: 'text-green-400', icon: DollarSign, label: 'Pago' },
      rejected: { bg: 'bg-red-500/20', text: 'text-red-400', icon: XCircle, label: 'Rejeitado' },
      cancelled: { bg: 'bg-gray-500/20', text: 'text-gray-400', icon: XCircle, label: 'Cancelado' }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  const hasPendingWithdrawal = data.withdrawals.some(w => w.status === 'pending');

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-[#1A1A1A] to-[#0D0D0D] border border-[#C79F5E]/20 rounded-2xl p-6 animate-pulse">
        <div className="h-6 bg-white/10 rounded w-1/3 mb-4"></div>
        <div className="h-20 bg-white/10 rounded"></div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-[#1A1A1A] to-[#0D0D0D] border border-[#C79F5E]/20 rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-500/20 to-[#C79F5E]/20 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center">
              <Wallet className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">Saques</h3>
              <p className="text-sm text-gray-400">Solicite o saque dos seus ganhos</p>
            </div>
          </div>
          <button
            onClick={loadData}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors text-gray-400"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Balance Cards */}
      <div className="p-6 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white/5 rounded-xl p-4">
            <p className="text-xs text-gray-400 uppercase mb-1">Total Ganhos</p>
            <p className="text-xl font-bold text-white">{formatCurrency(data.total_earnings)}</p>
          </div>
          <div className="bg-white/5 rounded-xl p-4">
            <p className="text-xs text-gray-400 uppercase mb-1">Total Sacado</p>
            <p className="text-xl font-bold text-gray-400">{formatCurrency(data.total_withdrawn)}</p>
          </div>
        </div>

        {/* Available Balance - Highlight */}
        <div className="bg-gradient-to-r from-green-500/20 to-green-600/10 border border-green-500/30 rounded-xl p-4">
          <p className="text-sm text-green-400 mb-1">Disponível para Saque</p>
          <p className="text-3xl font-bold text-white">{formatCurrency(data.available_balance)}</p>
          <p className="text-xs text-gray-400 mt-1">Mínimo: {formatCurrency(data.min_withdrawal)}</p>
        </div>

        {/* Request Button or Form */}
        {!showForm ? (
          <button
            onClick={() => setShowForm(true)}
            disabled={data.available_balance < data.min_withdrawal || hasPendingWithdrawal}
            className="w-full py-3 bg-gradient-to-r from-green-500 to-green-600 text-white font-bold rounded-xl hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <ArrowUpRight className="w-5 h-5" />
            Solicitar Saque
          </button>
        ) : (
          <form onSubmit={handleRequestWithdrawal} className="space-y-3">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Valor do Saque</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">R$</span>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  min={data.min_withdrawal}
                  max={data.available_balance}
                  step="0.01"
                  className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-3 text-white text-lg font-bold focus:border-green-500 focus:outline-none"
                  placeholder="0,00"
                  autoFocus
                />
              </div>
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>Mínimo: {formatCurrency(data.min_withdrawal)}</span>
                <button
                  type="button"
                  onClick={() => setAmount(data.available_balance.toString())}
                  className="text-green-400 hover:underline"
                >
                  Sacar tudo
                </button>
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => { setShowForm(false); setAmount(''); }}
                className="flex-1 py-3 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-all"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={requesting || !amount || parseFloat(amount) < data.min_withdrawal}
                className="flex-1 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white font-bold rounded-xl hover:opacity-90 transition-all disabled:opacity-50"
              >
                {requesting ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Confirmar'}
              </button>
            </div>
          </form>
        )}

        {/* Warning Messages */}
        {hasPendingWithdrawal && (
          <div className="flex items-start gap-2 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-yellow-200">Você já tem uma solicitação de saque pendente.</p>
          </div>
        )}

        {data.available_balance < data.min_withdrawal && !hasPendingWithdrawal && (
          <div className="flex items-start gap-2 p-3 bg-gray-500/10 border border-gray-500/30 rounded-lg">
            <AlertCircle className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-gray-300">
              Saldo mínimo para saque: {formatCurrency(data.min_withdrawal)}
            </p>
          </div>
        )}

        {/* Withdrawal History */}
        {data.withdrawals.length > 0 && (
          <div className="mt-6">
            <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
              <History className="w-4 h-4 text-[#C79F5E]" />
              Histórico de Saques
            </h4>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {data.withdrawals.map((w) => (
                <div
                  key={w.id}
                  className="bg-white/5 rounded-lg p-3 flex items-center justify-between"
                >
                  <div>
                    <p className="text-white font-semibold">{formatCurrency(w.amount)}</p>
                    <p className="text-xs text-gray-400">{formatDate(w.requested_at)}</p>
                    {w.rejection_reason && (
                      <p className="text-xs text-red-400 mt-1">{w.rejection_reason}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(w.status)}
                    {w.status === 'pending' && (
                      <button
                        onClick={() => handleCancelWithdrawal(w.id)}
                        className="p-1 hover:bg-red-500/20 rounded text-red-400 text-xs"
                      >
                        Cancelar
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WithdrawalSection;
